/**
 * AI Report Draft panel — right-docked, collapsible.
 *
 * Visual contract:
 *   - Header: specialty badge · "AI draft" pill · produced-at ago
 *   - Findings list: severity pill · anatomy · sentence · {accept, edit, reject}
 *     edited rows carry a small diff indicator so the AI contribution is
 *     auditable in the signed report.
 *   - Bottom: prose textarea + section templates, "Sign & send" button.
 *
 * Pattern lifted from a2zradiology.ai: AI writes the draft, human
 * accepts or edits. Findings already in `accepted` or `edited` state
 * compose the top of the Impression section; `rejected` are silently
 * dropped.
 */
import { createEffect, createMemo, createResource, createSignal, For, Show } from "solid-js";
import type { Specialty } from "../specialty/config";
import {
  fetchReportDraft, type AIFinding, type FindingState, type ReviewedFinding,
} from "./reportDraft";

export interface ReportDraftPanelProps {
  studyUid:  string;
  specialty: Specialty;
  /** Called when the user clicks a finding with a keyInstanceUid so
   *  the host viewer can swap active instance / frame. */
  onJumpTo?: (instanceUid: string, frame?: number) => void;
  /** Called when the reader commits a report body — parent signs
   *  + sends. */
  onSign?: (body: string) => Promise<void>;
  collapsed?: boolean;
  onCollapseChange?: (c: boolean) => void;
}

const SEV_CLASS: Record<AIFinding["severity"], string> = {
  critical:    "bg-danger/90 text-white",
  significant: "bg-warn/90 text-black",
  minor:       "bg-ink-300 text-ink-900",
  normal:      "bg-ink-200 text-ink-800",
};
const SEV_LABEL: Record<AIFinding["severity"], string> = {
  critical: "CRIT", significant: "SIG", minor: "MIN", normal: "OK",
};

export function ReportDraftPanel(p: ReportDraftPanelProps) {
  const [draft] = createResource(
    () => ({ study: p.studyUid, spec: p.specialty }),
    async ({ study, spec }) => fetchReportDraft(study, spec),
  );
  const [states, setStates] = createSignal<Record<string, ReviewedFinding>>({});

  // When the draft arrives, seed every finding as "pending".
  function ensureState(f: AIFinding): ReviewedFinding {
    const s = states()[f.id];
    if (s) return s;
    const next: ReviewedFinding = { ...f, state: "pending" };
    setStates((all) => ({ ...all, [f.id]: next }));
    return next;
  }

  function mark(id: string, patch: Partial<ReviewedFinding>) {
    setStates((all) => ({ ...all, [id]: { ...all[id], ...patch } }));
  }

  // Prose textarea — seeded from section templates once, edited
  // in-place, then extended when a finding is accepted.
  const [body, setBody] = createSignal("");
  function insertFinding(f: ReviewedFinding) {
    const line = (f.state === "edited" ? f.edited : f.sentence) ?? f.sentence;
    const cur = body();
    const prefix = cur.endsWith("\n") || cur === "" ? "" : "\n";
    setBody(cur + prefix + "• " + line + "\n");
  }

  // When the draft first becomes available, render the section headers
  // into the textarea so the reader sees the structure they'll fill in.
  //
  // Deliberately `createEffect`, not `createMemo`: this is a side-
  // effect (setBody writes a signal). Using createMemo here was the
  // "Maximum call stack size exceeded" offender — a memo that writes
  // a signal it also reads lets Solid's scheduler recurse through
  // the memo on every setBody, and the recursion depth composed with
  // other specialty shells mounting synchronously blows the JS
  // stack on routes where the tree is deeper (endoscopy after the
  // EndoscopyShell landed; pathology happened to tolerate it).
  createEffect(() => {
    const d = draft();
    if (!d || body() !== "") return;
    const seed = d.sections.map((s) => `## ${s.title}\n${s.template ?? ""}\n`).join("\n");
    setBody(seed);
  });

  function accept(id: string) {
    const s = states()[id];
    if (!s) return;
    mark(id, { state: "accepted" });
    insertFinding({ ...s, state: "accepted" });
  }
  function reject(id: string) { mark(id, { state: "rejected" }); }
  function edit(id: string, text: string) {
    mark(id, { state: "edited", edited: text });
  }

  const counts = () => {
    const s = states();
    let pending = 0, accepted = 0, edited = 0, rejected = 0;
    for (const k of Object.keys(s)) {
      const v = s[k];
      if (v.state === "pending")  pending++;
      else if (v.state === "accepted") accepted++;
      else if (v.state === "edited")   edited++;
      else if (v.state === "rejected") rejected++;
    }
    return { pending, accepted, edited, rejected };
  };

  function agoString(unix: number): string {
    const dt = Math.max(0, Math.floor(Date.now() / 1000) - unix);
    if (dt < 60) return `${dt}s ago`;
    if (dt < 3600) return `${Math.floor(dt / 60)}m ago`;
    return `${Math.floor(dt / 3600)}h ago`;
  }

  return (
    // NB: width is set via `style` not Tailwind utilities. Two
    // arbitrary-value classes (`w-[360px]` + `w-[32px]`) land in the
    // same specificity bucket, and source-order within a single class
    // attribute isn't deterministic across minified CSS — we saw the
    // collapsed panel keep its 360 px width. Inline style is
    // unambiguous.
    <aside class="flex h-full shrink-0 flex-col border-l border-ink-300 bg-ink-100"
           style={{ width: p.collapsed ? "32px" : "360px" }}>
      <div class="flex items-center gap-2 border-b border-ink-300 px-3 py-2">
        <button type="button"
                class="rounded p-0.5 text-ink-700 hover:text-white"
                title={p.collapsed ? "Expand AI panel" : "Collapse AI panel"}
                onClick={() => p.onCollapseChange?.(!p.collapsed)}>
          {p.collapsed ? "‹" : "›"}
        </button>
        <Show when={!p.collapsed}>
          <span class="chip-brand !text-[9px]">{p.specialty.toUpperCase()}</span>
          <span class="text-[10px] uppercase tracking-wider text-ink-700">AI draft</span>
          <Show when={draft()}>
            <span class="ml-auto text-[10px] text-ink-700">
              {agoString(draft()!.producedAt)}
            </span>
          </Show>
        </Show>
      </div>

      <Show when={!p.collapsed}>
        <Show when={!draft.loading} fallback={
          <div class="flex flex-1 items-center justify-center p-6 text-xs text-ink-700">
            <span class="inline-block h-2 w-2 animate-pulse rounded-full bg-brand align-middle" />
            <span class="ml-2">generating draft…</span>
          </div>
        }>
          <div class="border-b border-ink-300 px-3 py-1.5 text-[11px] text-ink-700">
            <span class="font-mono text-ink-900">{draft()!.findings.length}</span> findings
            {" · "}<span class="text-brand">{counts().accepted + counts().edited}</span> in report
            {" · "}<span class="text-danger">{counts().rejected}</span> rejected
          </div>
          <div class="flex-1 overflow-auto">
            <For each={draft()!.findings}>{(f) => {
              const st = () => ensureState(f);
              return (
                <div class="border-b border-ink-300 px-3 py-2 text-[11px]">
                  <div class="mb-1 flex items-center gap-2">
                    <span class={`rounded px-1.5 py-0.5 font-mono text-[9px] ${SEV_CLASS[f.severity]}`}>
                      {SEV_LABEL[f.severity]}
                    </span>
                    <span class="text-ink-900">{f.anatomy}</span>
                    <Show when={st().state !== "pending"}>
                      <span class="ml-auto text-[9px] uppercase tracking-wider"
                            classList={{
                              "text-brand":  st().state === "accepted",
                              "text-warn":   st().state === "edited",
                              "text-danger": st().state === "rejected",
                            }}>
                        {st().state}
                      </span>
                    </Show>
                    <Show when={f.keyInstanceUid}>
                      <button type="button"
                              class="rounded px-1 text-[10px] text-ink-700 hover:text-white"
                              title="Jump viewport to this finding"
                              onClick={() => p.onJumpTo?.(f.keyInstanceUid!, f.frameNumber)}>
                        ↗
                      </button>
                    </Show>
                  </div>
                  <Show when={st().state !== "edited"} fallback={
                    <textarea
                      class="w-full rounded border border-ink-300 bg-ink-200 px-2 py-1 text-[11px] leading-snug text-ink-900 focus:border-brand focus:outline-none"
                      rows="3"
                      value={st().edited ?? st().sentence}
                      onInput={(e) => edit(f.id, (e.currentTarget as HTMLTextAreaElement).value)}
                    />
                  }>
                    <p class="leading-snug text-ink-800"
                       classList={{ "line-through opacity-50": st().state === "rejected" }}>
                      {f.sentence}
                    </p>
                  </Show>
                  <div class="mt-2 flex gap-1">
                    <button type="button"
                            class="rounded border border-ink-300 px-2 py-0.5 text-[10px] uppercase tracking-wider hover:bg-brand hover:text-white hover:border-brand"
                            classList={{ "bg-brand text-white border-brand": st().state === "accepted" }}
                            onClick={() => accept(f.id)}>
                      accept
                    </button>
                    <button type="button"
                            class="rounded border border-ink-300 px-2 py-0.5 text-[10px] uppercase tracking-wider hover:bg-ink-200 hover:text-white"
                            classList={{ "bg-warn/20 text-warn border-warn": st().state === "edited" }}
                            onClick={() => mark(f.id, { state: "edited", edited: st().edited ?? f.sentence })}>
                      edit
                    </button>
                    <button type="button"
                            class="rounded border border-ink-300 px-2 py-0.5 text-[10px] uppercase tracking-wider hover:bg-danger/20 hover:text-danger hover:border-danger"
                            classList={{ "bg-danger/20 text-danger border-danger": st().state === "rejected" }}
                            onClick={() => reject(f.id)}>
                      reject
                    </button>
                    <Show when={st().state === "edited"}>
                      <button type="button"
                              class="ml-auto rounded border border-brand px-2 py-0.5 text-[10px] uppercase tracking-wider text-brand hover:bg-brand hover:text-white"
                              onClick={() => {
                                mark(f.id, { state: "accepted" });
                                insertFinding({ ...st(), state: "accepted" });
                              }}>
                        commit edit
                      </button>
                    </Show>
                  </div>
                </div>
              );
            }}</For>
          </div>

          <div class="border-t border-ink-300 p-3">
            <label class="mb-1 block text-[10px] uppercase tracking-wider text-ink-700">
              Report body
            </label>
            <textarea
              class="h-40 w-full resize-y rounded border border-ink-300 bg-ink-200 p-2 font-mono text-[11px] leading-snug text-ink-900 focus:border-brand focus:outline-none"
              value={body()}
              onInput={(e) => setBody((e.currentTarget as HTMLTextAreaElement).value)}
            />
            <div class="mt-2 flex gap-2">
              <button type="button"
                      class="btn-brand flex-1 text-xs"
                      onClick={() => p.onSign?.(body())}>
                Sign &amp; send
              </button>
              <button type="button"
                      class="btn-ghost text-xs"
                      onClick={() => navigator.clipboard.writeText(body())}>
                Copy
              </button>
            </div>
          </div>
        </Show>
      </Show>
    </aside>
  );
}
